import { AnyAction } from 'redux';
import { Currency } from '@synergy/models';

import {
    INVOICE_DETAIL_IS_CREDIT_CHANGED,
    INVOICE_DETAIL_RECIPIENT_TAX_RATE_ID_CHANGED,
    INVOICE_DETAIL_RESET,
} from '../invoice-detail-actions';
import { INVOICE_DETAIL_POSITIONS_RECEIVE, INVOICE_DETAIL_POSITIONS_SUBMIT } from './invoice-detail-positions-actions';
import { Invoice } from '../../../models/invoice';
import { InvoiceProductItem } from '../../../models/invoice-product-item';
import { PositionsFormValues } from '../../models/positions-form-values';
import { StepData } from '../../../../../common/components/new-stepper/step-data';
import { isInvoiceGenericItem, isInvoiceProductItem, isInvoiceTextItem } from '../../../models/invoice-item';

export interface InvoiceDetailPositionsReducerState {
    stepData: StepData<PositionsFormValues>;
    recipientTaxRateId: number | null;
    /**
     * The step as the server last handed it over (#2159). `stepData.values`
     * cannot serve: leaving a wizard step parks its input right there.
     */
    baseline?: PositionsFormValues;
    /** When the invoice went to BMD, if it did (#2326 R-239). */
    exportedToAccountingAt?: string | null;
}

export const initialInvoiceDetailPositionsReducerState: InvoiceDetailPositionsReducerState = {
    stepData: {
        errors: {},
        values: {
            netShippingCosts: 0,
            netSurcharge: 0,
            isTaxFree: false,
            allItems: [],
            supplier: null,
        },
        isValid: false,
        isDirty: false,
    },
    recipientTaxRateId: null,
};

export const invoiceDetailPositionsReducer = (
    state: InvoiceDetailPositionsReducerState = initialInvoiceDetailPositionsReducerState,
    action: AnyAction
): InvoiceDetailPositionsReducerState => {
    const { type } = action;

    switch (type) {
        case INVOICE_DETAIL_POSITIONS_SUBMIT: {
            const data = action['data'] as StepData<PositionsFormValues>;
            return {
                ...state,
                stepData: {
                    ...state.stepData,
                    ...data,
                },
            };
        }

        case INVOICE_DETAIL_POSITIONS_RECEIVE: {
            const invoice = action['invoice'] as Invoice;

            const values = {
                netShippingCosts: invoice.netShippingCosts,
                netSurcharge: invoice.netSurcharge,
                isTaxFree: invoice.isTaxFree,
                currency: invoice.currency as Currency,
                supplier: invoice.supplierAccount,

                allItems: [...invoice.items, ...invoice.textItems, ...invoice.surchargeItems].sort(
                    (a, b) => a.position - b.position
                ),
            };

            // Set taxrate Errors on receive
            // eslint-disable-next-line @typescript-eslint/no-explicit-any
            const errors = { allItems: [] as any[] };
            const taxRateError = values.allItems.some(item => !isInvoiceTextItem(item) && item.taxRate === undefined);
            values.allItems.forEach(
                (item, index) =>
                    (errors.allItems[index] =
                        !isInvoiceTextItem(item) && item.taxRate === undefined
                            ? { taxRate: 'app.error.required' }
                            : null)
            );

            return {
                ...state,
                stepData: {
                    ...state.stepData,
                    isValid: !taxRateError,
                    values,
                    // eslint-disable-next-line @typescript-eslint/no-explicit-any
                    errors: (taxRateError ? errors : {}) as any,
                },
                // The same values, kept apart from the ones the wizard edits.
                baseline: values,
                exportedToAccountingAt: invoice.exportedToAccountingAt ?? null,
            };
        }

        case INVOICE_DETAIL_RESET: {
            return initialInvoiceDetailPositionsReducerState;
        }

        case INVOICE_DETAIL_IS_CREDIT_CHANGED: {
            const nextItems = state.stepData.values.allItems.map(item => {
                if (isInvoiceProductItem(item)) {
                    return InvoiceProductItem.fromPlainObject({ ...item, price: (item?.price ?? 0) * -1 });
                } else if (isInvoiceGenericItem(item)) {
                    return {
                        ...item,
                        price: (item?.price ?? 0) * -1,
                    };
                }

                return item;
            });

            return {
                ...state,
                stepData: {
                    ...state.stepData,
                    values: {
                        ...state.stepData.values,

                        netShippingCosts: state.stepData.values.netShippingCosts * -1,
                        netSurcharge: state.stepData.values.netSurcharge * -1,
                        allItems: nextItems,
                    },
                    isDirty: true,
                },
            };
        }

        case INVOICE_DETAIL_RECIPIENT_TAX_RATE_ID_CHANGED: {
            return {
                ...state,
                recipientTaxRateId: action['recipientTaxRateId'],
            };
        }

        default: {
            return state;
        }
    }
};
